import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Select from "@/common/components/Select";
import TextField from "@/common/components/TextField";
import { Option } from "@/common/types/option";
import { MissionSchema } from "../schemas/mission.schema";
import { PlusIcon, X } from "lucide-react";
import { MissionRewardFieldError } from "../types/rewardErrors";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { MissionRewardType, MissionType } from "../types/mission.type";

type RewardFormValue = MissionSchema["rewards"][number] & {
  id: string;
  reward?: { id: string; display_name: string };
};
type AllowedRewardType = MissionRewardType;

type RewardsSectionProps = {
  rewards: RewardFormValue[];
  onAddReward: (payload?: {
    reward_id?: string;
    reward_label?: string;
    product_name?: string;
    quantity?: number;
    reward_type?: MissionRewardType;
  }) => void;
  onUpdateReward: (id: string, data: Partial<RewardFormValue>) => void;
  onRemoveReward: (id: string) => void;
  isSubmitting: boolean;
  rewardErrors?: MissionRewardFieldError[];
  rewardsListError?: string;
  productOptions: Option[];
  rafflesOptions: Option[];
  missionType: MissionType;
};

const sanitizeIntegerInput = (value: string) => value.replace(/[^0-9]/g, "");

const REWARD_TYPE_LABELS: Record<AllowedRewardType, string> = {
  product: "Produto",
  points: "Pontos",
  tickets: "Bilhetes",
};

const DEFAULT_REWARD_TYPES: Record<
  MissionSchema["mission_type"],
  AllowedRewardType[]
> = {
  establishment: ["product", "points", "tickets"],
  tcard: ["tickets"],
};

const resolveRewardProductValue = (reward?: RewardFormValue | null) => {
  if (!reward) return undefined;
  return reward.product_id ?? reward.reward_id ?? undefined;
};

const resolveRewardRaffleValue = (reward?: RewardFormValue | null) => {
  if (!reward) return undefined;
  return reward.product_id ?? reward.reward_id ?? undefined;
};

const resolveRewardTypeValue = (
  reward?: RewardFormValue | null,
  missionType?: MissionType
): AllowedRewardType => {
  if (!reward) return missionType === "tcard" ? "tickets" : "product";
  
  if (missionType === "tcard") return "tickets";

  if (
    reward.reward_type === "tickets" || 
    reward.reward_type === ("Tmais::Raffle" as any) ||
    reward.reward_type === ("Tmais::RewardTicket" as any) ||
    reward.reward_type === ("bilhetes" as any)
  ) {
    return "tickets";
  }

  if (reward.reward_type === "points") return "points";
  
  return "product";
};

export const RewardsSection = ({
  rewards,
  onAddReward,
  onUpdateReward,
  onRemoveReward,
  isSubmitting,
  rewardErrors,
  rewardsListError,
  productOptions,
  rafflesOptions,
  missionType,
}: RewardsSectionProps) => {
  const normalizedRewards = useMemo(
    () => rewards.map((reward, originalIndex) => ({ reward, originalIndex })),
    [rewards],
  );
  const visibleRewards = normalizedRewards;

  const selectedProductIdsSet = useMemo(() => {
    const set = new Set<string>();
    rewards.forEach((reward) => {
      if (resolveRewardTypeValue(reward, missionType) === "product") {
        const val = resolveRewardProductValue(reward);
        if (val) set.add(String(val));
      }
    });
    return set;
  }, [rewards, missionType]);

  const selectedRaffleIdsSet = useMemo(() => {
    const set = new Set<string>();
    rewards.forEach((reward) => {
      if (resolveRewardTypeValue(reward, missionType) === "tickets") {
        const val = resolveRewardRaffleValue(reward);
        if (val) set.add(String(val));
      }
    });
    return set;
  }, [rewards, missionType]);

  const availableProductOptions = useMemo(
    () =>
      productOptions.filter(
        (option) => !selectedProductIdsSet.has(String(option.value)),
      ),
    [productOptions, selectedProductIdsSet],
  );

  const availableRaffleOptions = useMemo(
    () =>
      rafflesOptions.filter(
        (option) => !selectedRaffleIdsSet.has(String(option.value)),
      ),
    [rafflesOptions, selectedRaffleIdsSet],
  );

  const getProductOptionsForReward = useCallback(
    (currentId?: string | null, currentReward?: RewardFormValue) => {
      const normalizedCurrentId = currentId ? String(currentId) : undefined;
      let filteredOptions = productOptions.filter((option) => {
        const value = String(option.value);
        if (normalizedCurrentId && value === normalizedCurrentId) return true;
        return !selectedProductIdsSet.has(value);
      });

      if (normalizedCurrentId) {
        const hasCurrent = filteredOptions.some((opt) => String(opt.value) === normalizedCurrentId);
        if (!hasCurrent) {
          filteredOptions = [
            ...filteredOptions,
            {
              value: normalizedCurrentId,
              label: currentReward?.reward?.display_name || currentReward?.reward_label || currentReward?.product_name || "Produto selecionado"
            }
          ];
        }
      }
      return filteredOptions;
    },
    [productOptions, selectedProductIdsSet],
  );

  const getRaffleOptionsForReward = useCallback(
    (currentId?: string | null, currentReward?: RewardFormValue) => {
      const normalizedCurrentId = currentId ? String(currentId) : undefined;
      let filteredOptions = rafflesOptions.filter((option) => {
        const value = String(option.value);
        if (normalizedCurrentId && value === normalizedCurrentId) return true;
        return !selectedRaffleIdsSet.has(value);
      });

      if (normalizedCurrentId) {
        const hasCurrent = filteredOptions.some((opt) => String(opt.value) === normalizedCurrentId);
        if (!hasCurrent) {
          filteredOptions = [
            ...filteredOptions,
            {
              value: normalizedCurrentId,
              label: currentReward?.reward?.display_name || currentReward?.reward_label || currentReward?.product_name || "Sorteio selecionado"
            }
          ];
        }
      }
      return filteredOptions;
    },
    [rafflesOptions, selectedRaffleIdsSet],
  );

  const [draftProduct, setDraftProduct] = useState<Option | null>(null);
  const [draftQuantityInput, setDraftQuantityInput] = useState("1");
  const [draftError, setDraftError] = useState<string | null>(null);
  const [isAddingReward, setIsAddingReward] = useState(false);
  const [draftRewardType, setDraftRewardType] = useState<AllowedRewardType>("product");

  const hasPointsReward = useMemo(
    () => rewards.some((reward) => resolveRewardTypeValue(reward, missionType) === "points"),
    [rewards, missionType],
  );

  const buildRewardTypeOptions = useCallback(
    (currentType?: AllowedRewardType) => {
      const baseTypes = DEFAULT_REWARD_TYPES[missionType] || DEFAULT_REWARD_TYPES.establishment;

      const allowPointsSelection = currentType === "points" 
        ? true 
        : !hasPointsReward;

      return baseTypes
        .filter((type) => type !== "points" || allowPointsSelection)
        .map((type) => ({
          value: type,
          label: REWARD_TYPE_LABELS[type] || type,
        }));
    },
    [hasPointsReward, missionType],
  );

  const rewardTypeOptions = useMemo(
    () => buildRewardTypeOptions(),
    [buildRewardTypeOptions],
  );

  useEffect(() => {
    if (!rewardTypeOptions.length) return;
    const hasDraft = rewardTypeOptions.some(
      (option) => option.value === draftRewardType,
    );
    if (!hasDraft) {
      setDraftRewardType(
        (rewardTypeOptions[0].value as AllowedRewardType) || "product",
      );
    }
  }, [draftRewardType, rewardTypeOptions]);

  useEffect(() => {
    setDraftProduct(null);
    setDraftError(null);
  }, [draftRewardType]);

  const getDefaultDraftRewardType = () =>
    (rewardTypeOptions[0]?.value as AllowedRewardType) || "product";

  const resetDraft = () => {
    setDraftProduct(null);
    setDraftQuantityInput("1");
    setDraftError(null);
    setDraftRewardType(getDefaultDraftRewardType());
  };

  const handleAddReward = () => {
    const isProductReward = draftRewardType === "product";
    const isTicketReward = draftRewardType === "tickets";
    const needsItemSelection = isProductReward || isTicketReward;

    if (needsItemSelection && !draftProduct) {
      setDraftError(isProductReward ? "Selecione um produto" : "Selecione um sorteio");
      return;
    }

    const parsedDraftQuantity = draftQuantityInput ? Number(draftQuantityInput) : 1;
    const quantity = Number.isFinite(parsedDraftQuantity) && parsedDraftQuantity > 0 ? parsedDraftQuantity : 1;

    onAddReward({
      reward_type: draftRewardType,
      reward_id: needsItemSelection && draftProduct ? String(draftProduct.value) : undefined,
      reward_label: needsItemSelection && draftProduct
          ? String(draftProduct.label)
          : REWARD_TYPE_LABELS[draftRewardType],
      product_name: needsItemSelection && draftProduct
          ? String(draftProduct.label)
          : undefined,
      quantity,
    });

    resetDraft();
    setIsAddingReward(false);
  };

  return (
    <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <h2 className="text-[22px] font-bold text-white">Recompensas</h2>
      </div>

      <div className="flex flex-col gap-4">
        {visibleRewards.length === 0 && (
          <span className="text-gray-400 text-sm">
            Adicione uma recompensa usando o formulário abaixo.
          </span>
        )}
        {visibleRewards.map(({ reward, originalIndex }, index) => {
          const errorsForReward = rewardErrors?.[originalIndex];
          const normalizedRewardType = resolveRewardTypeValue(reward, missionType);
          const isTicketType = normalizedRewardType === "tickets";
          
          const rewardValue = isTicketType 
            ? resolveRewardRaffleValue(reward)
            : resolveRewardProductValue(reward);

          const optionsForReward = isTicketType
            ? getRaffleOptionsForReward(rewardValue, reward)
            : getProductOptionsForReward(rewardValue, reward);

          const typeOptionsForReward = buildRewardTypeOptions(normalizedRewardType);
          const rewardTypeValue =
            typeOptionsForReward.find(
              (option) => option.value === normalizedRewardType,
            ) || null;
            
          const rewardGridColumns =
            normalizedRewardType === "product" || isTicketType 
              ? "md:grid-cols-3"
              : "md:grid-cols-2";

          return (
            <div
              key={reward.id || `reward-${index}`}
              className="flex bg-neutral-op-8 rounded-md overflow-hidden min-h-[84px]"
            >
              <div className="w-[4px] bg-[#2b88d8] shrink-0" />

              <div className="flex items-center justify-center px-4 shrink-0">
                <button
                  type="button"
                  onClick={() => onRemoveReward(reward.id)}
                  className="text-danger hover:text-red-400 transition-colors"
                  disabled={isSubmitting}
                >
                  <X size={24} strokeWidth={1.5} />
                </button>
              </div>

              <div
                className={`flex-1 p-4 grid grid-cols-1 ${rewardGridColumns} gap-6 items-start`}
              >
                <Select
                  label="Tipo de recompensa"
                  placeholder="Selecione o tipo"
                  options={typeOptionsForReward}
                  value={rewardTypeValue}
                  onChange={(option) => {
                    const normalized = option
                      ? (option as Option)
                      : ({ value: rewardTypeValue?.value } as Option);
                    const nextType = String(normalized.value) as AllowedRewardType;
                    onUpdateReward(reward.id, { reward_type: nextType });
                  }}
                  isDisabled={isSubmitting}
                />
                
                {normalizedRewardType === "product" ? (
                  <Select
                    label="Produto"
                    placeholder="Selecione um produto"
                    options={optionsForReward}
                    value={
                      rewardValue
                        ? optionsForReward.find(
                            (option) => String(option.value) === String(rewardValue),
                          ) || { label: reward.reward?.display_name || reward.reward_label || reward.product_name || "Produto selecionado", value: rewardValue }
                        : null
                    }
                    onChange={(option) =>
                      onUpdateReward(reward.id, {
                        reward_id: option ? String((option as Option).value) : undefined,
                        reward_label: option ? String((option as Option).label) : "",
                        product_name: option ? String((option as Option).label) : "",
                      })
                    }
                    isDisabled={isSubmitting || optionsForReward.length === 0}
                  />
                ) : null}

                {isTicketType ? (
                  <Select
                    label="Sorteio"
                    placeholder="Selecione um sorteio"
                    options={optionsForReward}
                    value={
                      rewardValue
                        ? optionsForReward.find(
                            (option) => String(option.value) === String(rewardValue),
                          ) || { label: reward.reward?.display_name || reward.reward_label || reward.product_name || "Sorteio selecionado", value: rewardValue }
                        : null
                    }
                    onChange={(option) =>
                      onUpdateReward(reward.id, {
                        reward_id: option ? String((option as Option).value) : undefined,
                        reward_label: option ? String((option as Option).label) : "",
                        product_name: option ? String((option as Option).label) : "",
                      })
                    }
                    isDisabled={isSubmitting || optionsForReward.length === 0}
                  />
                ) : null}

                <TextField
                  label="Quantidade"
                  type="text"
                  inputMode="numeric"
                  value={
                    reward.quantity === undefined || reward.quantity === null
                      ? ""
                      : String(reward.quantity)
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const sanitized = sanitizeIntegerInput(event.target.value);
                    if (!sanitized) {
                      onUpdateReward(reward.id, { quantity: undefined });
                      return;
                    }
                    const parsed = Number(sanitized);
                    if (!Number.isFinite(parsed) || parsed <= 0) {
                      onUpdateReward(reward.id, { quantity: undefined });
                      return;
                    }
                    onUpdateReward(reward.id, { quantity: parsed });
                  }}
                  onBlur={(event: ChangeEvent<HTMLInputElement>) => {
                    if (!sanitizeIntegerInput(event.target.value)) {
                      onUpdateReward(reward.id, { quantity: 1 });
                    }
                  }}
                  errorMessage={errorsForReward?.quantity?.message}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          );
        })}

        {rewardsListError && (
          <ErrorMessage name="rewards_list" message={rewardsListError} />
        )}
      </div>

      {isAddingReward ? (
        <div className="bg-neutral-op-8 rounded-md p-4 flex flex-col gap-4">
          <Select
            label="Tipo"
            placeholder="Selecione o tipo"
            options={rewardTypeOptions}
            value={
              rewardTypeOptions.find(
                (option) => option.value === draftRewardType,
              ) || null
            }
            onChange={(option) => {
              const normalized = option ? (option as Option) : null;
              const normalizedValue = normalized
                ? (String(normalized.value) as AllowedRewardType)
                : getDefaultDraftRewardType();
              setDraftRewardType(normalizedValue);
              setDraftError(null);
            }}
            isDisabled={isSubmitting}
          />

          {draftRewardType === "product" ? (
            <Select
              label="Produto"
              placeholder="Selecione o produto"
              options={availableProductOptions}
              value={draftProduct}
              onChange={(option) => {
                const normalized = option ? (option as Option) : null;
                setDraftProduct(normalized);
                setDraftError(null);
              }}
              isDisabled={
                isSubmitting || availableProductOptions.length === 0
              }
            />
          ) : null}

          {draftRewardType === "tickets" ? (
            <Select
              label="Sorteio"
              placeholder="Selecione o sorteio"
              options={availableRaffleOptions}
              value={draftProduct}
              onChange={(option) => {
                const normalized = option ? (option as Option) : null;
                setDraftProduct(normalized);
                setDraftError(null);
              }}
              isDisabled={
                isSubmitting || availableRaffleOptions.length === 0 
              }
            />
          ) : null}

          {(draftRewardType !== "product" ||
            (draftRewardType === "product" && draftProduct)) && (
            <div className="grid grid-cols-1 gap-4">
              <TextField
                label="Quantidade"
                type="text"
                inputMode="numeric"
                value={draftQuantityInput}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const sanitized = sanitizeIntegerInput(event.target.value);
                  setDraftQuantityInput(sanitized);
                }}
                onBlur={() => {
                  if (!draftQuantityInput) {
                    setDraftQuantityInput("1");
                  }
                }}
                disabled={isSubmitting}
              />
            </div>
          )}

          {draftError && (
            <ErrorMessage name="draft_reward" message={draftError} />
          )}

          <div className="flex flex-col gap-2 md:flex-row md:justify-end md:gap-4">
            <Button
              type="button"
              variant="outline-text"
              onClick={() => {
                setIsAddingReward(false);
                resetDraft();
              }}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="outline-text"
              onClick={handleAddReward}
              disabled={
                isSubmitting ||
                (draftRewardType === "product" && (!draftProduct || availableProductOptions.length === 0)) ||
                (draftRewardType === "tickets" && (!draftProduct || availableRaffleOptions.length === 0))
              }
            >
              <span className="flex items-center gap-2">
                <PlusIcon size={18} /> Salvar recompensa
              </span>
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            resetDraft();
            setIsAddingReward(true);
          }}
          className="bg-neutral-op-8 rounded-md px-4 py-3 text-left text-gray-300 border border-dashed border-gray-600 hover:border-white transition-colors"
          disabled={isSubmitting}
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            <PlusIcon size={18} /> Adicionar recompensa
          </span>
        </button>
      )}
    </Box>
  );
};